import {
  Dialog,
  DialogActions,
  DialogTitle,
  Link,
  Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useDispatch } from "react-redux";
import {
  getDeviceType,
  getToken,
  getUser,
  requestErrorHandler,
  USER_GROUPS,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { showPopupSnackbar } from "../../redux/actions/snackbarActions";

const server = EnvSettings.server;
const EDITABLE_DEVICE_FIELDS = ["plugType", "color", "chassisNumber"];
const DEVICE_FORM_FIELDS = [
  "chassisNumber",
  "plugType",
  "color",
  "model",
  "version",
  "deviceName",
  "location",
  "macAddress",
  "teamviewerId",
  "deviceType",
  "OrderId",
];

const getDeviceFormValue = (deviceData, field) => {
  if (field === "deviceType") {
    return getDeviceType(deviceData?.serialNumber) || "";
  }
  return deviceData?.[field] ?? "";
};

const getEditableDevicePayload = (deviceData) =>
  EDITABLE_DEVICE_FIELDS.reduce(
    (payload, field) => ({
      ...payload,
      [field]: deviceData?.[field] ?? null,
    }),
    { serialNumber: deviceData?.serialNumber },
  );

function ConfirmDeleteDialog({ showDialog, deleteOrder, onClose }) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={showDialog}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Delete Device</DialogTitle>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined" autoFocus>
          {t("no")}
        </Button>
        <Button onClick={deleteOrder} color="secondary" variant="contained">
          {t("yes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DeviceDataForm({ deviceData, updateLocalDevice }) {
  const [edit, setEdit] = useState(false);
  const [editedData, setEditedData] = useState(deviceData);
  const [showDelete, setShowDelete] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    setEditedData(deviceData);
    setEdit(false);
  }, [deviceData]);

  const submitChanges = (del_dev) => {
    if (
      !del_dev &&
      (!deviceData || !editedData || shallowEqual(editedData, deviceData))
    ) {
      return;
    }
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        deviceData: del_dev ? editedData : getEditableDevicePayload(editedData),
        ...(del_dev ? del_dev : {}),
      }),
    };
    fetch(server + "/edit_device", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          dispatch(showPopupSnackbar(result));
          setEdit(false);
          if (del_dev) {
            window.location.reload();
          }
          if (updateLocalDevice) {
            updateLocalDevice(editedData);
          }
        }
      });
  };

  return editedData ? (
    <Grid container spacing={3}>
      <ConfirmDeleteDialog
        onClose={() => {
          setShowDelete(false);
        }}
        showDialog={showDelete}
        deleteOrder={() => {
          submitChanges({ deleteDevice: true });
        }}
      />
      {DEVICE_FORM_FIELDS.map((field) => {
        const editable = EDITABLE_DEVICE_FIELDS.includes(field);
        if (field === "OrderId") {
          return (
            deviceData?.Order && (
              <Grid item xs={12} sm={3} key={field}>
                <Typography>{t("deviceList.reservedForOrder")}</Typography>
                <Link href={"/cpanel/orders/" + deviceData?.Order.number}>
                  {deviceData.Order.number}
                </Link>
              </Grid>
            )
          );
        }
        return (
          <Grid item xs={12} sm={3} key={field}>
            <TextField
              fullWidth
              label={t("deviceList." + field)}
              value={getDeviceFormValue(editedData, field)}
              variant="outlined"
              color={editable ? "primary" : undefined}
              disabled={!editable || !edit}
              focused={editable && edit}
              onChange={(e) => {
                if (!editable) {
                  return;
                }
                setEditedData({
                  ...editedData,
                  [field]: e.target.value || null,
                });
              }}
            />
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <Button
          variant="outlined"
          onClick={() => {
            if (edit) {
              setEditedData({ ...deviceData });
            }
            setEdit(!edit);
          }}
        >
          {edit ? t("cancel") : t("edit")}
        </Button>
        {!edit || shallowEqual(editedData, deviceData) ? null : (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              submitChanges();
            }}
          >
            {t("save")}
          </Button>
        )}
        {edit && getUser().permissionlevel === USER_GROUPS.SUPER_USER ? (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setShowDelete(true);
            }}
          >
            {t("delete")}
          </Button>
        ) : null}
      </Grid>
    </Grid>
  ) : null;
}
